import {createWixEventHandler} from './common/logger/handlers/wix-bi';
import {createClientTranslateFn} from './common/translate';
import {createAnswersAnalyticsHandler} from './common/logger/handlers/analytics';
import {createLogger} from './common/logger/logger';
import 'babel-polyfill';
import 'es6-promise/auto';

import * as React from 'react';
import { render } from 'react-dom';
import { SourceType, SourceTypeUtils } from '@wix/answers-api';
import { WidgetContainer } from './widget-view/widget-container';

import './widget-view/style.global.scss';
import { EventType } from './common/logger/event-type';
import { createBiEventHandler } from './common/logger/handlers/bi';
import { useRouterHistory } from 'react-router';
import { createHistory } from 'history';
import { createSentryReporter } from '@wix/answers-lib';
import * as sentry from '@sentry/browser';
import { getAnonId } from '@wix/answers-bi';

interface AugmentedWindow extends Window {
	__INITIAL_STATE__: any;
	__BASE_PATH__: string;
	__answersWidgetSdk: any;
	__wixOnlyWidgetSdk: any;
	__STATICS_URL: string;
}

const augmentedWindow = (window as unknown) as AugmentedWindow;

const basePath = augmentedWindow.__BASE_PATH__;

const rootElement = document.getElementById('root');

const {
	locale, apiUrl, categoryTree, widgetId, homepageArticles, config, isPreview, isMobile
} = augmentedWindow.__INITIAL_STATE__;
const history = useRouterHistory(createHistory)({ basename: basePath });
const translate = createClientTranslateFn(locale);

const props = { locale, apiUrl, categoryTree, history, widgetId, homepageArticles, config, isPreview, isMobile};
const source = SourceTypeUtils.sourceTypeToString(SourceType.WIDGET, widgetId || '');
const analyticsHandler = createAnswersAnalyticsHandler(apiUrl, SourceType.WIDGET, widgetId || '', locale);
const biEventHandler = createBiEventHandler(widgetId, locale, config.tenant.id);

export const getWidgetVersion = (aw: AugmentedWindow) => {
	try {
		const matches = aw.__STATICS_URL.match(/\d+\.\d+\.\d+/);
		return matches ? matches[0] : 'error-parsing-release-a';
	} catch (e) {
		return 'error-parsing-release-b';
	}
};

const logger = createLogger();
logger.addEventHandler(analyticsHandler);
logger.addEventHandler(biEventHandler);

const errorReporter = createSentryReporter(sentry, 'https://bd4caf4e00b74b0eb5a7b98bd8a14ada@sentry.wixpress.com/64');

errorReporter.setup(getWidgetVersion(augmentedWindow), {
	denyUrls: [/https:\/\/d2x3xhvgiqkx42\.cloudfront\.net/]
});

try {
	errorReporter.indentify({id: getAnonId() || ''});
} catch (e) {
	// do nothing
}

const createCustomHandlerGetterSetter = () => {
	let customHomePageCb: any = null;
	let customHeaderCb: any = null;
	let customHeroHtmlCb: any = null;
	let customFieldsCb: any = null;
	let identifyUserCb: any = null;
	let goToArticleCb: any = null;
	let sendChatMessageCb: any = null;
	let onChatStartedCb: any = null;
	return {
		setCustomHomePageHandler: (cb: ((html: string) => void)) => {
			customHomePageCb = cb;
		},
		getCustomHomePageHandler: () => customHomePageCb,
		setCustomHeaderHandler: (cb: ((html: string) => void)) => {
			customHeaderCb = cb;
		},
		getCustomHeaderHandler: () => customHeaderCb,
		setCustomHeroHtmlHandler: (cb: ((html: string) => void)) => {
			customHeroHtmlCb = cb;
		},
		getCustomHeroHtmlHandler: () => customHeroHtmlCb,
		setCustomFieldsHandler: (cb: ((customFields: {[key: string]: string}) => void)) => {
			customFieldsCb = cb;
		},
		getCustomFieldsHandler: () => customFieldsCb,
		setIdentifyUserHandler: (cb: ((email: string) => void)) => {
			identifyUserCb = cb;
		},
		getIdentifyUserHandler: () => identifyUserCb,
		setGoToArticleHandler: (cb: ((articleId: string) => void)) => {
			goToArticleCb = cb;
		},
		getGoToArticleHandler: () => goToArticleCb,
		setSendChatMessageHandler: (cb: ((message: {content: string}) => void)) => {
			sendChatMessageCb = cb;
		},
		getSendChatMessageHandler: () => sendChatMessageCb,
		setOnChatStartedHandler: (cb: ((callback: (ticketId: string) => void) => void)) => {
			onChatStartedCb = cb;
		},
		getOnChatStartedHandler: () => onChatStartedCb
	};
};

const customHandlerGetterSetter = createCustomHandlerGetterSetter();

augmentedWindow.__answersWidgetSdk = {
	eventType: EventType,
	addEventHandler: (handler: any) => {
		logger.addEventHandler(handler);
	},
	setCustomHomePage: (html: string) => {
		if (customHandlerGetterSetter.getCustomHomePageHandler()) {
			customHandlerGetterSetter.getCustomHomePageHandler()(html);
		} else {
			// tslint:disable-next-line:no-console
			console.error('called sdk method before handler was set. Make sure you do this after it is set');
		}
	},
	setCustomHeader: (html: string) => {
		if (customHandlerGetterSetter.getCustomHeaderHandler()) {
			customHandlerGetterSetter.getCustomHeaderHandler()(html);
		} else {
			// tslint:disable-next-line:no-console
			console.error('called sdk method before handler was set. Make sure you do this after it is set');
		}
	},
	addCustomHeroHtml: (html: string) => {
		if (customHandlerGetterSetter.getCustomHeroHtmlHandler()) {
			customHandlerGetterSetter.getCustomHeroHtmlHandler()(html);
		} else {
			// tslint:disable-next-line:no-console
			console.error('called sdk method before handler was set. Make sure you do this after it is set');
		}
	},
	setCustomFields: (customFields: {[key: string]: string}) => {
		if (customHandlerGetterSetter.getCustomFieldsHandler()) {
			customHandlerGetterSetter.getCustomFieldsHandler()(customFields);
		} else {
			// tslint:disable-next-line:no-console
			console.error('called sdk method before handler was set. Make sure you do this after it is set');
		}
	},
	identifyUser: (email: string) => {
		if (customHandlerGetterSetter.getIdentifyUserHandler()) {
			customHandlerGetterSetter.getIdentifyUserHandler()(email);
		} else {
			// tslint:disable-next-line:no-console
			console.error('called sdk method before handler was set. Make sure you do this after it is set');
		}
	},
	goToArticle: (articleId: string) => {
		if (customHandlerGetterSetter.getGoToArticleHandler()) {
			customHandlerGetterSetter.getGoToArticleHandler()(articleId);
		} else {
			// tslint:disable-next-line:no-console
			console.error('called sdk method before handler was set. Make sure you do this after it is set');
		}
	},
	sendChatMessage: (message: string) => {
		if (customHandlerGetterSetter.getSendChatMessageHandler()) {
			customHandlerGetterSetter.getSendChatMessageHandler()({content: message});
		} else {
			// tslint:disable-next-line:no-console
			console.error('called sdk method before handler was set. Make sure you do this after it is set');
		}
	},
	onChatStarted: (cb: (ticketId: string) => void) => {
		if (customHandlerGetterSetter.getOnChatStartedHandler()) {
			customHandlerGetterSetter.getOnChatStartedHandler()(cb);
		} else {
			// tslint:disable-next-line:no-console
			console.error('called sdk method before handler was set. Make sure you do this after it is set');
		}
	}
};

augmentedWindow.__wixOnlyWidgetSdk = {
	addWixBiEventHandler: () => {
		const wixEventHandler = createWixEventHandler(source, locale);
		logger.addEventHandler(wixEventHandler);
	},
	forceVisibilityStatus: (newStatus: boolean) => {
		logger.setVisibility(newStatus);
	}
};

try {
	(rootElement as HTMLElement).innerHTML = '';

	const comp = (
		<WidgetContainer
			{...props}
			baseUrl={basePath}
			logger={logger}
			translate={translate}
			customHandlerGetterSetter={customHandlerGetterSetter}
		/>
	);
	render(comp, rootElement as HTMLElement);
} catch (e) {
	// tslint:disable-next-line:no-console
	console.error('Error rendering widget', {e});
	errorReporter.reportError(e);
}
